import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, defer, interval, of } from 'rxjs';
import {
    catchError,
    filter,
    map,
    mergeMap,
    switchMap,
    takeUntil,
    takeWhile,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import { DevicesActions } from '../actions/devices.actions';
import { API_PING_INTERVAL, USER_TYPES } from '../constants/common.constants';
import {
    selectDevicesList,
    selectDevicesWithLocation,
    selectSectedDevice,
} from '../selectors/devices.selectors';
import { selectPingDeviceLocationStatus } from '../selectors/status.selectors';
import { selectUser } from '../selectors/user.selector';
import { ApiService, GoogleMapService, LocalStorageService } from '../services';
import { IAppState } from '../state/app.state';

@Injectable()
export class LocationsEffects {
    /// ***** Device Location ***** ///
    pingDeviceLocationInterval$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.startPingDeviceLocation),
            switchMap(() => {
                let pingInterval: number;
                if (this.localStorage.getItem('Fast-Traking-Started')) {
                    pingInterval = 10000;
                } else if (this.localStorage.getItem('PingInterval-devices')) {
                    pingInterval = +this.localStorage.getItem('PingInterval-devices');
                } else {
                    pingInterval = API_PING_INTERVAL;
                }
                return interval(pingInterval).pipe(
                    withLatestFrom(this.store.select(selectPingDeviceLocationStatus)),
                    takeWhile(([, pingDeviceLocation]) => pingDeviceLocation),
                    withLatestFrom(this.store.select(selectUser), this.store.select(selectSectedDevice)),
                    filter(([, , device]) => !(device && device.history && !!device.history.length)),
                    mergeMap(([, user, device]) =>
                        this.apiService.getDeviceLocation(user.account_id, device.info.device_id).pipe(
                            map((locations: string) =>
                                DevicesActions.pingDeviceLocationSuccess({
                                    response: JSON.parse(locations)[0],
                                }),
                            ),
                            catchError((error) => of(DevicesActions.pingDeviceLocationError({ error }))),
                        ),
                    ),
                );
            }),
        ),
    );

    pingDeviceLocationSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DevicesActions.pingDeviceLocationSuccess),
                withLatestFrom(this.store.select(selectSectedDevice)),
                filter(([, selectedDevice]) => !!selectedDevice.location_ping),
                tap(([, selectedDevice]) => {
                    this.googleMapService.updateSelectedDeviceMarkerPosition(
                        selectedDevice,
                        selectedDevice.settings.preferences.follow_device,
                    );

                    this.googleMapService.updateSelectedDeviceMarkerIcon(selectedDevice);
                }),
                filter(() => this.router.url.split('/').slice(1).length === 3), // Get Addres only on info page
                tap(([, device]) =>
                    this.store.dispatch(
                        DevicesActions.getAddressForDevice({
                            deviceID: device.info.device_id,
                            lat: device.location_ping.lat,
                            lng: device.location_ping.lng,
                        }),
                    ),
                ),
            ),
        { dispatch: false },
    );

    /// ***** Devices Locations ***** ///
    pingDevicesLocationsInterval$ = createEffect(() =>
        this.actions$.pipe(
            filter(() => !Boolean(this.localStorage.getItem('GQL_LOCATION'))),
            ofType(DevicesActions.getDevicesSuccess, DevicesActions.changePingDeviceLocationInterval),
            withLatestFrom(this.store.select(selectDevicesList)),
            filter(([, devices]) => !!devices.length && this.router.url === '/map/devices'),
            switchMap(() => {
                let pingInterval: number;
                if (this.localStorage.getItem('Fast-Traking-Started')) {
                    pingInterval = 10000;
                } else if (this.localStorage.getItem('PingInterval-devices')) {
                    pingInterval = +this.localStorage.getItem('PingInterval-devices');
                } else {
                    pingInterval = API_PING_INTERVAL;
                }
                return interval(pingInterval).pipe(
                    takeUntil(this.actions$.pipe(ofType(DevicesActions.clearDevices))),
                    withLatestFrom(this.store.select(selectUser)),
                    mergeMap(([, user]) =>
                        this.apiService
                            .getDevicesLocations(user.account_id, USER_TYPES.ONLY_MASTER_ACCOUNT)
                            .pipe(
                                filter((response) => !!response.length),
                                map((response) => DevicesActions.pingDevicesLocationsSuccess({ response })),
                                catchError((error) =>
                                    of(DevicesActions.pingDevicesLocationsError({ error })),
                                ),
                            ),
                    ),
                );
            }),
        ),
    );

    getDevicesLocationsSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DevicesActions.pingDevicesLocationsSuccess),
                withLatestFrom(this.store.select(selectDevicesWithLocation)),
                filter(
                    ([, devices]) =>
                        !!devices.length &&
                        this.router.url.indexOf('rings') === -1 &&
                        this.router.url !== '/multimap',
                ),
                tap(([, devices]) => {
                    const devicesWithUpdatedLocation = devices.filter((device) => device.location_ping);
                    this.googleMapService.updateMarkersPositions(devicesWithUpdatedLocation);
                }),
            ),
        { dispatch: false },
    );

    liveTracking$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(DevicesActions.liveTracking),
                withLatestFrom(this.store.select(selectUser), this.store.select(selectDevicesList)),
                switchMap(([action, user]) => {
                    const timeOut = 900; // 15 min
                    const pingInterval = 5; // 5 sec
                    return this.apiService
                        .startLiveTracking(user.account_id, timeOut, pingInterval, action.deviceIDs)
                        .pipe(
                            map(() => DevicesActions.liveTrackingSuccess()),
                            catchError((error) => of(DevicesActions.liveTrackingError({ error }))),
                        );
                }),
            ),
        { dispatch: false },
    );

    startStopLiveTracking$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DevicesActions.liveTrackingSuccess, DevicesActions.stopLiveTracking),
            withLatestFrom(this.store.select(selectUser)),
            switchMap(([action, user]) => {
                return this.apiService
                    .getDevicesLocations(user.account_id, USER_TYPES.ONLY_MASTER_ACCOUNT)
                    .pipe(
                        filter((response) => !!response.length),
                        map((response) =>
                            DevicesActions.pingDevicesLocationsSuccess({
                                response: response.map((location) => {
                                    return {
                                        ...location,
                                        is_fast_tracking_enabled:
                                            action.type === '[Devices API] Device Live Tracking',
                                    };
                                }),
                            }),
                        ),
                        catchError((error) => of(DevicesActions.pingDevicesLocationsError({ error }))),
                    );
            }),
        ),
    );

    constructor(
        private actions$: Actions,
        private apiService: ApiService,
        private store: Store<IAppState>,
        private googleMapService: GoogleMapService,
        private router: Router,
        private localStorage: LocalStorageService,
    ) {}
}
